import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChartBar,
  PaperPlaneTilt,
  UsersThree,
  ShieldCheck,
  ChatCircle,
  Envelope,
  DownloadSimple,
} from '@phosphor-icons/react'
import { cn } from '@/lib/utils'
import { handleTabListKeyDown } from '@/lib/rovingTabs'
import { analyticsApi } from '@/lib/api'
import { Select } from '@/components/ui/Select'
import { DotLoader } from '@/components/ui/DotLoader'
import { usePermission } from '@/hooks/usePermission'
import { exportAnalyticsOverview } from './exportAnalytics'
import type { Period, AnalyticsTab } from '@/types/analytics'
import DateRangePicker from '@/components/ui/DateRangePicker'
import OverviewTab from './components/OverviewTab'
import CampaignsTab from './components/CampaignsTab'
import ContactsTab from './components/ContactsTab'
import DeliverabilityTab from './components/DeliverabilityTab'
import RepliesTab from './components/RepliesTab'

type TabIcon = React.ComponentType<{ className?: string; weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone' }>

// The picker never resolves these to dates — the token goes straight to the API,
// which owns the window arithmetic (and the previous-period comparison window).
const PERIOD_PRESETS: { label: string; value: Period }[] = [
  { label: 'Last 7 days',  value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 90 days', value: '90d' },
  { label: 'All time',     value: 'all' },
]

const DEFAULT_PERIOD: Period = '30d'

const tabs: { id: AnalyticsTab; label: string; icon: TabIcon }[] = [
  { id: 'overview',       label: 'Overview',       icon: ChartBar },
  { id: 'campaigns',      label: 'Campaigns',      icon: PaperPlaneTilt },
  { id: 'contacts',       label: 'Contacts',       icon: UsersThree },
  { id: 'deliverability', label: 'Deliverability', icon: ShieldCheck },
  { id: 'replies',        label: 'Replies',        icon: ChatCircle },
]

const DEFAULT_TAB: AnalyticsTab = 'overview'

/**
 * The URL owns the active tab, so whatever it holds has to be validated. The
 * raw value used to be cast straight to `AnalyticsTab`, so `?tab=bogus` marked
 * no tab active in the strip AND matched none of the render branches below —
 * the page rendered its chrome over an empty body with no way back other than
 * editing the URL. Anything unrecognised, empty or missing resolves to the
 * first tab.
 */
export function resolveAnalyticsTab(raw: string | null | undefined): AnalyticsTab {
  return tabs.some((t) => t.id === raw) ? (raw as AnalyticsTab) : DEFAULT_TAB
}

/** Dropdown value meaning "do not filter". A sentinel rather than '' so the
 *  Select always has a real option selected, and rather than undefined so the
 *  combined view is a deliberate choice instead of an absent one. */
export const ALL_INBOXES = '__all__'

/** The value the API takes for the mailbox dropdown: undefined is the combined
 *  view, which every builder treats as "no filter". */
export function resolveMailboxFilter(value: string): string | undefined {
  return value === ALL_INBOXES ? undefined : value
}

/** The views a mailbox can actually scope. Contacts is absent on purpose: it
 *  counts contacts, which no mailbox sent, so offering the control there would
 *  imply a narrowing that cannot happen. Overview is absent for the same
 *  reason its builder takes no mailbox. */
export const MAILBOX_SCOPED_TABS = ['campaigns', 'deliverability', 'replies']

/** Hidden until there is a real choice: with one mailbox, "All mailboxes" and
 *  that mailbox are the same view. */
export function showsMailboxFilter(tab: string, optionCount: number): boolean {
  return MAILBOX_SCOPED_TABS.includes(tab) && optionCount > 1
}

const AnalyticsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = resolveAnalyticsTab(searchParams.get('tab'))
  const activeTabIndex = tabs.findIndex((t) => t.id === activeTab)

  // analytics.view is "all" for Owner/Admin/Analyst (team-wide numbers + the
  // account filter) and "own" only for Representative, who sees just their own
  // numbers — so the account dropdown is not rendered for that role at all.
  // The API enforces this; hiding the filter keeps it from 403-ing.
  const { scope: analyticsScope } = usePermission('analytics', 'view')
  const canViewTeamAnalytics = analyticsScope === 'all'
  const { allowed: canExport } = usePermission('analytics', 'export')
  const [exporting, setExporting] = useState(false)

  const [period, setPeriod] = useState<Period | null>(DEFAULT_PERIOD)
  const [startDate, setStartDate] = useState<string | null>(null)
  const [endDate, setEndDate] = useState<string | null>(null)
  // The ONE account filter for every tab: null = all teammates sharing the
  // user's mailbox addresses (team-wide totals, matching the Inbox scope).
  const [accountId, setAccountId] = useState<number | null>(null)

  // SMTP mailbox filter (Campaigns tab), beside the account filter as a segmented
  // control. The user's configured sender addresses; scoping by ADDRESS means a
  // shared mailbox surfaces every sharing user's campaigns. Declared before the
  // account query because the account list cascades from the selected mailbox.
  // The addresses analytics can actually be scoped by, from analytics itself.
  //
  // This used to read /sender-emails/addresses, which lists mailboxes that
  // still EXIST. Deleting a mailbox leaves its campaigns behind with a snapshot
  // of the address, and the campaign filter matches that snapshot -- so the
  // dropdown offered no option that could reach them. On one account 22
  // campaigns were unreachable under every option in the list, which also
  // emptied the account dropdown's cascade. /analytics/mailboxes answers from
  // the campaigns, union the caller's own live mailboxes so nothing that was
  // selectable before disappears.
  const { data: senders, isLoading: sendersLoading } = useQuery({
    queryKey: ['analytics-scope-mailboxes'],
    queryFn: () => analyticsApi.getScopeMailboxes().then((r) => r.data.items),
    staleTime: 5 * 60 * 1000,
  })
  const smtpOptions = useMemo(() => {
    const seen = new Set<string>()
    // "All mailboxes" first and selected by default: the combined view is the
    // honest starting point, and it is what every tab but Campaigns has always
    // shown. The page used to default to the account's default sender, which
    // silently hid every campaign sent from any other mailbox.
    const opts: { value: string; label: string; description?: string }[] = [
      { value: ALL_INBOXES, label: 'All mailboxes' },
    ]
    for (const s of senders ?? []) {
      if (!s.email_address || seen.has(s.email_address)) continue
      seen.add(s.email_address)
      opts.push({
        value: s.email_address,
        label: s.email_address,
        description: s.label && s.label !== s.email_address ? s.label : undefined,
      })
    }
    return opts
  }, [senders])
  // The dropdown's value, which is ALL_INBOXES rather than undefined so the
  // Select renders a chosen option. `mailbox` below is what the API takes:
  // undefined for the combined view.
  const [senderEmail, setSenderEmail] = useState<string>(ALL_INBOXES)
  const mailbox = resolveMailboxFilter(senderEmail)

  // Change the mailbox → the account list changes with it, and the account
  // selection resets to "All accounts" so a stale account isn't kept.
  const onSenderEmailChange = useCallback((v: string) => {
    setSenderEmail(v)
    setAccountId(null)
  }, [])

  // The account dropdown cascades from the mailbox: on the Campaigns tab it lists
  // only accounts that use the selected SMTP address; elsewhere, every member of
  // the account.
  const teamScopeSender = activeTab === 'campaigns' ? mailbox : undefined
  const { data: teamMembers, isLoading: teamMembersLoading } = useQuery({
    queryKey: ['analytics', 'team-members', teamScopeSender],
    queryFn: () =>
      analyticsApi
        .getTeamMembers(teamScopeSender ? { sender_email: teamScopeSender } : undefined)
        .then((r) => r.data.items),
    staleTime: 5 * 1000,
    // A Representative only ever sees their own numbers, so the roster is
    // neither offered nor fetched for them — the endpoint returns just
    // themselves anyway, and the API refuses a teammate user_id.
    enabled: canViewTeamAnalytics,
  })

  // The account list is re-scoped whenever `teamScopeSender` changes — which
  // happens on a mailbox change AND on every hop on/off the Campaigns tab. A
  // selection made against the wider team-wide list can therefore survive into a
  // narrower mailbox-scoped list that no longer contains it: the Select found no
  // matching option and fell back to its "Select..." placeholder while every tab
  // was still being filtered by that invisible account. Drop it instead.
  useEffect(() => {
    if (!teamMembers || accountId === null) return
    if (!teamMembers.some((m) => m.user_id === accountId)) setAccountId(null)
  }, [teamMembers, accountId])

  const accountOptions = useMemo(
    () => [
      { value: '', label: 'All accounts' },
      ...(teamMembers ?? []).map((m) => ({
        value: String(m.user_id),
        // Name on the main line; email on the Select's secondary line (smaller,
        // muted, non-bold) instead of cramming "Name (email)" onto one row.
        label: m.name || m.email,
        description: m.name ? m.email : undefined,
      })),
    ],
    [teamMembers],
  )

  const showMailboxFilter = showsMailboxFilter(activeTab, smtpOptions.length)

  // Send exactly the filters on screen. `accountId` is only ever set by a
  // role that may use it, and the backend re-checks it regardless.
  const handleExport = async () => {
    setExporting(true)
    try {
      await exportAnalyticsOverview({
        ...(period ? { period } : {}),
        ...(startDate ? { start: startDate } : {}),
        ...(endDate ? { end: endDate } : {}),
        ...(accountId !== null ? { user_id: accountId } : {}),
      })
    } finally {
      setExporting(false)
    }
  }

  // §55 — the page's own pending work is the two lists the scope filters are
  // built from. Announced once, here; each tab announces its own fetches.
  // The team query is disabled for own-scoped roles, and a disabled query
  // never leaves its loading state — so it must not feed the spinner, or the
  // header would show "Loading analytics filters" forever for a Rep.
  const scopeLoading =
    (canViewTeamAnalytics && teamMembersLoading) || (activeTab === 'campaigns' && sendersLoading)

  // Only `tab` is rewritten — `setSearchParams({ tab })` replaced the whole
  // query string, so any other param on the URL was silently dropped by a tab
  // click.
  const handleTabChange = useCallback((tab: AnalyticsTab) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      params.set('tab', tab)
      return params
    })
  }, [setSearchParams])

  const handlePeriodChange = useCallback((p: Period) => {
    setPeriod(p)
    setStartDate(null)
    setEndDate(null)
  }, [])

  const handleCustomRange = useCallback((start: string, end: string) => {
    setPeriod(null)
    setStartDate(start)
    setEndDate(end)
  }, [])

  return (
    <div className="space-y-5">
      {/* Header — §23 */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1
            className="text-foreground dark:text-[#f7f8f8]"
            style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
          >
            Analytics
          </h1>
          <p
            className="text-muted-foreground dark:text-[#8a8f98] mt-1"
            style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
          >
            Track performance and optimize your email marketing.
          </p>
        </div>
        <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto">
          {/* Account + SMTP mailbox as one segmented control (Inbox chrome).
              The mailbox half shows on the Campaigns tab, which it scopes.

              Below `sm` the two halves stack. Side by side they need ~420px of
              intrinsic width and the wrapper is `overflow-hidden`, so on a narrow
              viewport the account half was simply cut off at the rounded edge
              (and a long sender address pushed the whole header past the page
              width). The widths are Tailwind classes rather than the `minWidth`
              prop precisely so they can be per-breakpoint. */}
          {/* The whole segmented control disappears when neither half applies
              (an own-scoped role off the Campaigns tab) — an empty bordered
              shell is worse than no control. */}
          {(showMailboxFilter || canViewTeamAnalytics) && (
            <div
              className={cn(
                'flex w-full min-w-0 flex-col items-stretch overflow-hidden rounded-lg border',
                'sm:h-9 sm:w-auto sm:flex-row sm:items-center',
                'border-border/60 bg-background',
                'dark:border-white/[0.08] dark:bg-white/[0.02]',
                'focus-within:border-[#7170ff]/55 dark:focus-within:border-[#7170ff]/75 transition-colors',
              )}
            >
              {/* Mailbox (SMTP) comes first — it scopes which accounts appear next. */}
              {showMailboxFilter && (
                <Select
                  value={senderEmail}
                  onChange={onSenderEmailChange}
                  ariaLabel="Mailbox (SMTP sender)"
                  icon={<Envelope className="h-4 w-4" weight="regular" />}
                  options={smtpOptions}
                  className="w-full min-w-0 sm:w-[220px]"
                  buttonClassName="border-transparent bg-transparent rounded-none hover:bg-muted/40 dark:bg-transparent dark:hover:bg-white/[0.04]"
                />
              )}
              {/* Only a real divider when both halves are present. Stacked: a
                  full-width hairline between the rows; inline: a vertical rule. */}
              {showMailboxFilter && canViewTeamAnalytics && (
                <div
                  className="h-px w-full flex-shrink-0 bg-border/60 dark:bg-white/[0.08] sm:h-5 sm:w-px"
                  aria-hidden="true"
                />
              )}
              {canViewTeamAnalytics && (
                <Select
                  value={accountId === null ? '' : String(accountId)}
                  onChange={(v) => setAccountId(v === '' ? null : Number(v))}
                  ariaLabel="Account filter"
                  options={accountOptions}
                  className="w-full min-w-0 sm:w-[200px]"
                  buttonClassName="border-transparent bg-transparent rounded-none hover:bg-muted/40 dark:bg-transparent dark:hover:bg-white/[0.04]"
                />
              )}
            </div>
          )}
          {scopeLoading && (
            <span className="inline-flex h-9 items-center px-1 text-muted-foreground dark:text-[#8a8f98]">
              <DotLoader size="sm" label="Loading analytics filters" />
            </span>
          )}
          <DateRangePicker
            label="Date range"
            presets={PERIOD_PRESETS}
            activePreset={period}
            onPresetChange={(v) => handlePeriodChange(v as Period)}
            startDate={startDate}
            endDate={endDate}
            onApply={handleCustomRange}
            // There is no "no date filter" state here — clearing a custom range
            // falls back to the default window rather than sending none.
            onClear={() => handlePeriodChange(DEFAULT_PERIOD)}
          />
          {/* Exports the Overview KPIs for exactly the filters shown. Offered
              on the Overview tab only, since that is what the workbook holds. */}
          {canExport && activeTab === 'overview' && (
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              className="inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-lg border border-border/60 bg-background px-3 text-foreground transition-colors hover:bg-muted/40 disabled:opacity-60 dark:border-white/[0.08] dark:bg-white/[0.02] dark:text-[#f7f8f8] dark:hover:bg-white/[0.04]"
              style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
            >
              <DownloadSimple className="h-4 w-4" weight="bold" />
              {exporting ? 'Exporting…' : 'Export'}
            </button>
          )}
        </div>
      </div>

      {/* Tab Navigation — indigo brand active border (matches ContactDetailModal tabs).
          Full ARIA tab pattern via the shared roving-tabindex helper: the strip is
          one tab stop and Arrow / Home / End move between tabs (selection follows
          focus, as it already does for clicks). */}
      <div
        className="flex flex-wrap gap-1 border-b border-border/50 dark:border-white/[0.06]"
        role="tablist"
        aria-label="Analytics section"
        onKeyDown={(e) =>
          handleTabListKeyDown(e, {
            activeIndex: activeTabIndex,
            count: tabs.length,
            onSelect: (i) => handleTabChange(tabs[i].id),
          })
        }
      >
        {tabs.map(({ id, label, icon: Icon }) => {
          const active = activeTab === id
          return (
            <button
              key={id}
              type="button"
              role="tab"
              id={`analytics-tab-${id}`}
              aria-controls={`analytics-panel-${id}`}
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              onClick={() => handleTabChange(id)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-2.5 border-b-2 transition-colors duration-150 whitespace-nowrap -mb-px',
                active
                  ? 'border-[#5e6ad2] text-[#5e6ad2] dark:border-[#7170ff] dark:text-[#a5b4fc]'
                  : 'border-transparent text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8]',
              )}
              style={{ fontSize: '13px', fontWeight: active ? 590 : 510, letterSpacing: '-0.13px' }}
            >
              <Icon className="h-3.5 w-3.5" weight={active ? 'bold' : 'regular'} />
              {label}
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          role="tabpanel"
          id={`analytics-panel-${activeTab}`}
          aria-labelledby={`analytics-tab-${activeTab}`}
          // Focusable so keyboard users can Tab straight from the strip into the
          // panel's charts and tables instead of skipping past them.
          tabIndex={0}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.15 }}
        >
          {activeTab === 'overview' && (
            <OverviewTab period={period} startDate={startDate} endDate={endDate} userId={accountId} />
          )}
          {activeTab === 'campaigns' && (
            <CampaignsTab
              period={period}
              startDate={startDate}
              endDate={endDate}
              userId={accountId}
              senderEmail={mailbox}
              mailboxOptions={smtpOptions}
              mailboxValue={senderEmail}
              onMailboxChange={onSenderEmailChange}
            />
          )}
          {activeTab === 'contacts' && (
            <ContactsTab period={period} startDate={startDate} endDate={endDate} userId={accountId} />
          )}
          {activeTab === 'deliverability' && (
            <DeliverabilityTab period={period} startDate={startDate} endDate={endDate} userId={accountId} senderEmail={mailbox} />
          )}
          {activeTab === 'replies' && (
            <RepliesTab period={period} startDate={startDate} endDate={endDate} userId={accountId} senderEmail={mailbox} />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

export default AnalyticsPage
